// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * RESEARCH §2's hard limits, as data — one `LimitRule` per check the linter can run.
 *
 * The sharpest finding in the product lives here: **Anthropic's 20 / 24 / 16 are request-wide
 * aggregates, not per-tool budgets.** Anthropic's own words: "if you have 4 strict tools with 6
 * optional parameters each, you'll reach the 24-parameter limit even though no single tool seems
 * complex." A linter that divides these by tool count, or checks them per tool, reports a clean bill
 * of health on a belt that 400s. `scope: 'request'` is the flag that keeps that honest.
 *
 * A green check on these means "passes the published limits". It never means "will compile" —
 * Anthropic has unpublished internal compiled-grammar-size limits and a 180-second compilation
 * timeout beyond them.
 */

import type { Provenance, TargetId } from '../types.ts';

export type LimitScope =
  /** Counted once across the whole request — every tool in the belt, summed. */
  | 'request'
  /** Counted within one tool's schema. */
  | 'tool'
  /** A property of a single node or name. */
  | 'node';

export type LimitUnit =
  | 'properties'
  | 'levels'
  | 'characters'
  | 'enum-values'
  | 'tools'
  | 'parameters'
  | 'digits'
  | 'seconds'
  | 'nodes';

export interface LimitRule {
  readonly id: string;
  readonly targets: readonly TargetId[];
  /** One line, addressed to the user, for the finding message. */
  readonly label: string;
  readonly value: number;
  readonly unit: LimitUnit;
  readonly scope: LimitScope;
  /**
   * True when the number is a sum across every tool in the request. The three Anthropic budgets are
   * the only aggregates in the table and the only ones a per-tool linter gets wrong.
   */
  readonly aggregate: boolean;
  /** The provider's own words, where they exist. Never paraphrased. */
  readonly quote?: string;
  readonly note?: string;
  readonly provenance: Provenance;
}

const OPENAI_ALL: readonly TargetId[] = [
  'openai-strict',
  'openai-nonstrict-responses',
  'openai-nonstrict-chat',
];

const ANTHROPIC_STRICT: readonly TargetId[] = ['anthropic-strict', 'anthropic-sdk'];

const GEMINI_ALL: readonly TargetId[] = ['gemini-parameters', 'gemini-jsonschema', 'vertex-parameters'];

export const LIMITS: readonly LimitRule[] = [
  // ── OpenAI (DOC, structured-outputs.md L1694-1706) ───────────────────────────────────────────────
  {
    id: 'openai.properties',
    targets: OPENAI_ALL,
    label: 'Total object properties across the schema',
    value: 5000,
    unit: 'properties',
    scope: 'tool',
    aggregate: false,
    note: 'The widely-cited 100-property figure is OBSOLETE. It survives on Microsoft\'s Azure Foundry page and in most blog posts; ship 5000 and surface 100 only inside the Azure warning.',
    provenance: 'DOC',
  },
  {
    id: 'openai.nesting',
    targets: OPENAI_ALL,
    label: 'Levels of nesting',
    value: 10,
    unit: 'levels',
    scope: 'tool',
    aggregate: false,
    note: 'The widely-cited five-level figure is OBSOLETE — same Azure provenance as the 100-property one.',
    provenance: 'DOC',
  },
  {
    id: 'openai.total-chars',
    targets: OPENAI_ALL,
    label: 'Total characters across all property names, definition names, enum values and const values',
    value: 120_000,
    unit: 'characters',
    scope: 'tool',
    aggregate: false,
    provenance: 'DOC',
  },
  {
    id: 'openai.enum-values',
    targets: OPENAI_ALL,
    label: 'Enum values across all enum properties',
    value: 1000,
    unit: 'enum-values',
    scope: 'tool',
    aggregate: false,
    provenance: 'DOC',
  },
  {
    id: 'openai.large-enum-chars',
    targets: OPENAI_ALL,
    label: 'Characters of enum strings, once a single string enum exceeds 250 values',
    value: 15_000,
    unit: 'characters',
    scope: 'tool',
    aggregate: false,
    note: 'Conditional: this budget only applies to a string enum with MORE THAN 250 members. Below that threshold there is no character cap of its own.',
    provenance: 'DOC',
  },
  {
    id: 'openai.large-enum-threshold',
    targets: OPENAI_ALL,
    label: 'Enum members that trigger the 15,000-character enum-string budget',
    value: 250,
    unit: 'enum-values',
    scope: 'node',
    aggregate: false,
    provenance: 'DOC',
  },
  {
    id: 'openai.name-length',
    targets: OPENAI_ALL,
    label: 'Function name length',
    value: 64,
    unit: 'characters',
    scope: 'node',
    aggregate: false,
    provenance: 'DOC',
  },

  // ── Anthropic (DOC, structured-outputs.md § "Schema complexity limits") ─────────────────────────
  //
  // "The following limits apply to all requests with output_config.format or strict: true".
  // ALL THREE ARE REQUEST-WIDE AGGREGATES. Do not check them per tool.
  {
    id: 'anthropic.strict-tools',
    targets: ANTHROPIC_STRICT,
    label: 'Tools with strict:true in one request',
    value: 20,
    unit: 'tools',
    scope: 'request',
    aggregate: true,
    quote: "Non-strict tools don't count toward this limit.",
    note: 'Consequence the UI must state: a pasted MCP tools/list of 40 tools can never be all-strict on Anthropic.',
    provenance: 'DOC',
  },
  {
    id: 'anthropic.optional-params',
    targets: ANTHROPIC_STRICT,
    label: 'Optional parameters across ALL strict tool schemas and JSON output schemas in one request',
    value: 24,
    unit: 'parameters',
    scope: 'request',
    aggregate: true,
    quote: 'Each parameter not listed in `required` counts toward this limit.',
    note: 'Anthropic\'s advantage — optional properties are legal, unlike OpenAI strict — is capped at 24 across the whole belt. Anthropic\'s own guidance: "Reduce optional parameters… Each optional parameter roughly doubles a portion of the grammar\'s state space." Their example: 4 strict tools with 6 optional parameters each already hits the limit though no single tool looks complex.',
    provenance: 'DOC',
  },
  {
    id: 'anthropic.union-params',
    targets: ANTHROPIC_STRICT,
    label: 'Parameters using anyOf OR a type array, across ALL strict schemas in one request',
    value: 16,
    unit: 'parameters',
    scope: 'request',
    aggregate: true,
    quote: 'especially expensive because they create exponential compilation cost',
    note: 'A `["string","null"]` type array counts here exactly as an anyOf does — so a belt converted for OpenAI strict, where every optional field becomes a type array, blows this budget immediately.',
    provenance: 'DOC',
  },
  {
    id: 'anthropic.name-length',
    targets: ['anthropic-strict', 'anthropic-nonstrict', 'anthropic-sdk'],
    label: 'Tool name length',
    value: 64,
    unit: 'characters',
    scope: 'node',
    aggregate: false,
    provenance: 'DOC',
  },
  {
    id: 'anthropic.deferred-tools',
    targets: ['anthropic-strict', 'anthropic-nonstrict', 'anthropic-sdk'],
    label: 'Deferred tools per request under tool search',
    value: 10_000,
    unit: 'tools',
    scope: 'request',
    aggregate: true,
    provenance: 'DOC',
  },
  {
    id: 'anthropic.compilation-timeout',
    targets: ANTHROPIC_STRICT,
    label: 'Grammar compilation timeout',
    value: 180,
    unit: 'seconds',
    scope: 'request',
    aggregate: false,
    note: 'A hard timeout on top of the unpublished compiled-grammar-size limit. Passing the published limits is not a promise that your belt compiles.',
    provenance: 'DOC',
  },

  // ── Gemini / Vertex ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'gemini.name-length',
    targets: GEMINI_ALL,
    label: 'Function name length',
    value: 128,
    unit: 'characters',
    scope: 'node',
    aggregate: false,
    provenance: 'DOC',
  },

  // ── GBNF (SRC, common/json-schema-to-grammar.cpp) ──────────────────────────────────────────────
  {
    id: 'gbnf.number-integral-digits',
    targets: ['gbnf-llamacpp', 'gbnf-xgrammar'],
    label: 'Digits in the integral part of a generated number',
    value: 16,
    unit: 'digits',
    scope: 'node',
    aggregate: false,
    note: 'Arbitrarily long numbers are not representable by the emitted grammar. Assert this at runtime.',
    provenance: 'SRC',
  },
  {
    id: 'gbnf.number-fraction-digits',
    targets: ['gbnf-llamacpp', 'gbnf-xgrammar'],
    label: 'Digits in the fractional part of a generated number',
    value: 16,
    unit: 'digits',
    scope: 'node',
    aggregate: false,
    provenance: 'SRC',
  },
  {
    id: 'gbnf.integer-bound-digits',
    targets: ['gbnf-llamacpp', 'gbnf-xgrammar'],
    label: 'Digits an unbounded integer side compiles to (build_min_max_int decimals_left)',
    value: 16,
    unit: 'digits',
    scope: 'node',
    aggregate: false,
    note: 'A hard cap, not a default you can raise: {"type":"integer","minimum":0} compiles to ([0] | [1-9] [0-9]{0,15}). So minimum:0 does NOT admit every non-negative integer.',
    provenance: 'SRC',
  },
  {
    id: 'gbnf.whitespace-newlines',
    targets: ['gbnf-llamacpp', 'gbnf-xgrammar'],
    label: 'Consecutive newlines the `space` rule permits',
    value: 2,
    unit: 'characters',
    scope: 'node',
    aggregate: false,
    note: 'space ::= | " " | "\\n"{1,2} [ \\t]{0,20} — whitespace is capped at 1-2 newlines plus 20 spaces or tabs.',
    provenance: 'SRC',
  },
  {
    id: 'gbnf.whitespace-indent',
    targets: ['gbnf-llamacpp', 'gbnf-xgrammar'],
    label: 'Spaces or tabs of indentation the `space` rule permits',
    value: 20,
    unit: 'characters',
    scope: 'node',
    aggregate: false,
    provenance: 'SRC',
  },

  // ── Our own budget, not a provider's ────────────────────────────────────────────────────────────
  {
    id: 'toolwright.schema-nodes',
    targets: [
      'openai-strict',
      'openai-nonstrict-responses',
      'openai-nonstrict-chat',
      'anthropic-strict',
      'anthropic-nonstrict',
      'anthropic-sdk',
      'gemini-parameters',
      'gemini-jsonschema',
      'vertex-parameters',
      'gbnf-llamacpp',
      'gbnf-xgrammar',
    ],
    label: 'Schema nodes we will normalise before refusing',
    value: 4000,
    unit: 'nodes',
    scope: 'request',
    aggregate: true,
    note: 'NOT a provider limit. openai-agents-python guards $ref expansion at _MAX_SCHEMA_NODES = 100_000, sized for a server-side Python SDK and explicitly aimed at "tool schemas advertised by a third-party MCP server" — precisely this tool\'s input. Do not copy 100,000 into a browser: budget in the low thousands and fail visibly with "schema too large to normalise". And do not eagerly expand $ref at all — the SDK only unravels a $ref carrying sibling keys, precisely so {"$ref":"#"} terminates.',
    provenance: 'SDK',
  },
];

const LIMITS_BY_ID: ReadonlyMap<string, LimitRule> = new Map(LIMITS.map((l) => [l.id, l]));

export function limitById(id: string): LimitRule | undefined {
  return LIMITS_BY_ID.get(id);
}

export function limitsFor(target: TargetId): readonly LimitRule[] {
  return LIMITS.filter((l) => l.targets.includes(target));
}

/**
 * The three strict-mode budgets Anthropic sums across the WHOLE request — the ones a per-tool linter
 * gets wrong. Named explicitly rather than filtered, so adding another request-scoped limit later
 * cannot silently join this set.
 */
export const ANTHROPIC_REQUEST_BUDGETS: readonly LimitRule[] = [
  'anthropic.strict-tools',
  'anthropic.optional-params',
  'anthropic.union-params',
].map((id) => {
  const rule = LIMITS_BY_ID.get(id);
  if (!rule) throw new Error(`limits.ts: ${id} is missing from LIMITS`);
  return rule;
});

const value = (id: string): number => {
  const rule = LIMITS_BY_ID.get(id);
  if (!rule) throw new Error(`limits.ts: ${id} is missing from LIMITS`);
  return rule.value;
};

/**
 * Convenience views for the linter's hot paths. Derived from `LIMITS`, never re-typed — change the
 * number in one place and both the check and the UI table move together.
 */
export const OPENAI_LIMITS = {
  maxProperties: value('openai.properties'),
  maxNesting: value('openai.nesting'),
  maxChars: value('openai.total-chars'),
  maxEnumValues: value('openai.enum-values'),
  maxLargeEnumChars: value('openai.large-enum-chars'),
  largeEnumThreshold: value('openai.large-enum-threshold'),
  maxNameLength: value('openai.name-length'),
} as const;

/**
 * ALL THREE OF THESE ARE REQUEST-WIDE. Sum them across the whole belt, never per tool — that is the
 * distinction the `LimitRule.scope` field exists to carry, and the one a convenience object like this
 * makes it easy to forget.
 */
export const ANTHROPIC_LIMITS = {
  maxStrictTools: value('anthropic.strict-tools'),
  maxOptionalParams: value('anthropic.optional-params'),
  maxUnionParams: value('anthropic.union-params'),
  maxNameLength: value('anthropic.name-length'),
  compilationTimeoutSeconds: value('anthropic.compilation-timeout'),
} as const;

export const GEMINI_LIMITS = {
  maxNameLength: value('gemini.name-length'),
} as const;

// ── Name charsets ────────────────────────────────────────────────────────────────────────────────

export interface NameRule {
  readonly targets: readonly TargetId[];
  /** Anchored, no `g` flag — safe to reuse. */
  readonly pattern: RegExp;
  readonly maxLength: number;
  /** The provider's own words. */
  readonly quote: string;
  readonly provenance: Provenance;
  readonly note?: string;
}

export const NAME_RULES: readonly NameRule[] = [
  {
    targets: OPENAI_ALL,
    pattern: /^[a-zA-Z0-9_-]{1,64}$/,
    maxLength: 64,
    quote: 'a-zA-Z0-9_-, max 64',
    provenance: 'DOC',
  },
  {
    targets: ['anthropic-strict', 'anthropic-nonstrict', 'anthropic-sdk'],
    pattern: /^[a-zA-Z0-9_-]{1,64}$/,
    maxLength: 64,
    quote: '^[a-zA-Z0-9_-]{1,64}$',
    provenance: 'DOC',
    note: 'The tightest charset of any target. An MCP-style `server.tool_name` or `ns:tool` — extremely common in a pasted tools/list — is legal on Gemini and ILLEGAL here. Renaming breaks call-id linkage unless you emit the name-mapping table alongside it.',
  },
  {
    targets: ['gemini-parameters', 'gemini-jsonschema'],
    pattern: /^[a-zA-Z0-9_:.-]{1,128}$/,
    maxLength: 128,
    quote:
      'Must be a-z, A-Z, 0-9, or contain underscores, colons, dots, and dashes, with a maximum length of 128.',
    provenance: 'DOC',
    note: 'Contradiction inside the SAME discovery document: FunctionCall.name and FunctionResponse.name describe only underscores and dashes — no colons or dots — at the same 128 maximum. A name with a dot may therefore be legal to declare and unrepresentable to call back.',
  },
  {
    targets: ['vertex-parameters'],
    pattern: /^[a-zA-Z_][a-zA-Z0-9_:.-]{0,127}$/,
    maxLength: 128,
    quote:
      'Must start with a letter or an underscore. Must be a-z, A-Z, 0-9, or contain underscores, dots, colons and dashes, with a maximum length of 128.',
    provenance: 'DOC',
    note: 'Vertex adds the leading-character rule the Gemini API does not state.',
  },
];

export function nameRuleFor(target: TargetId): NameRule | undefined {
  return NAME_RULES.find((r) => r.targets.includes(target));
}

// ── Anthropic tools-system-prompt overhead ───────────────────────────────────────────────────────

/**
 * DOC, the tool-use pricing table: tokens added on top of your serialized tools, per model.
 * "the table assumes at least 1 tool is provided"; with no tools and tool_choice:none it is 0.
 */
export interface ToolsSystemPromptRow {
  readonly model: string;
  /** tool_choice: auto | none */
  readonly auto: number;
  /** tool_choice: any | tool */
  readonly any: number;
}

export const ANTHROPIC_TOOLS_SYSTEM_PROMPT_TOKENS: readonly ToolsSystemPromptRow[] = [
  { model: 'Opus 5', auto: 286, any: 406 },
  { model: 'Opus 4.8', auto: 290, any: 410 },
  { model: 'Opus 4.7', auto: 675, any: 804 },
  { model: 'Opus 4.6', auto: 497, any: 589 },
  { model: 'Opus 4.5', auto: 496, any: 588 },
  { model: 'Sonnet 5', auto: 354, any: 474 },
  { model: 'Sonnet 4.6', auto: 497, any: 589 },
  { model: 'Haiku 4.5', auto: 496, any: 588 },
];

export const ANTHROPIC_TOOLS_SYSTEM_PROMPT_NOTE =
  'These tokens are added on top of your serialized tools. The table assumes at least one tool is provided; with no tools and tool_choice:none the overhead is 0.';

// ── Literal errors that are not keyword-specific ─────────────────────────────────────────────────

export const ERR_ANTHROPIC_TOO_COMPLEX = 'Schema is too complex for compilation.';
export const ERR_ANTHROPIC_TOOL_RESULT_ORDER =
  'tool_use ids were found without tool_result blocks immediately after';
export const ANTHROPIC_UNSUPPORTED_BLANKET =
  "If you use an unsupported feature, you'll receive a 400 error with details.";
export const ERR_OPENAI_OBSOLETE_PARAM_LIMIT =
  "Invalid schema for function 'X': 413 parameters exceeds limit of 100.";

// ── Things with NO published number ──────────────────────────────────────────────────────────────

/**
 * Assert the ABSENCE explicitly, so nobody later invents a number. A linter that invents one is
 * asserting a number that does not exist.
 */
export const NO_PUBLISHED_CAP: readonly { readonly targets: readonly TargetId[]; readonly what: string }[] = [
  { targets: ['anthropic-nonstrict'], what: 'non-strict tool count per request' },
  { targets: ['anthropic-nonstrict'], what: 'total schema size' },
  {
    targets: ['gemini-parameters', 'gemini-jsonschema', 'vertex-parameters'],
    what: 'functionDeclarations per request (Tool.functionDeclarations carries no maxItems)',
  },
  { targets: ['gemini-parameters', 'gemini-jsonschema', 'vertex-parameters'], what: 'total schema size' },
];

/** Soft guidance, never a check. Advisory copy only. */
export const SOFT_GUIDANCE: readonly { readonly targets: readonly TargetId[]; readonly quote: string }[] = [
  {
    targets: ['anthropic-strict', 'anthropic-nonstrict', 'anthropic-sdk'],
    quote: "Claude's ability to pick the right tool degrades once you exceed 30–50 available tools",
  },
  {
    targets: ['anthropic-strict', 'anthropic-nonstrict', 'anthropic-sdk'],
    quote: '~55k tokens in definitions',
  },
  {
    targets: ['gemini-parameters', 'gemini-jsonschema', 'vertex-parameters'],
    quote: 'Keep active set to 10-20 tools maximum.',
  },
];

// ── Azure: a warning profile, never a transform ───────────────────────────────────────────────────

/**
 * RESEARCH §2 is emphatic. Azure's page is self-contradictory and carries `ai-usage: ai-assisted`:
 * it says Azure supports the same subset as OpenAI immediately above a table that contradicts
 * openai.com. Quote both texts and let the user decide.
 *
 * NEVER ship an "Azure-conservative" transform that strips `pattern`/`minimum`/`minItems`. Silently
 * deleting a working constraint on the authority of one self-contradicting AI-assisted page is worse
 * than warning. That is why `azure-openai` is not a `TargetId`.
 */
export const AZURE_DIVERGENCE = {
  claim: 'Azure OpenAI structured outputs support the same subset of the JSON Schema as OpenAI',
  tableSays: {
    objectProperties: 100,
    nestingLevels: 5,
    unsupported: {
      string: ['minlength', 'maxLength', 'pattern', 'format'],
      number: ['minimum', 'maximum', 'multipleOf'],
      objects: [
        'patternProperties',
        'unevaluatedProperties',
        'propertyNames',
        'minProperties',
        'maxProperties',
      ],
      arrays: [
        'unevaluatedItems',
        'contains',
        'minContains',
        'maxContains',
        'minItems',
        'maxItems',
        'uniqueItems',
      ],
    },
    alsoSays: 'Structured outputs are not supported with parallel function calls.',
  },
  notOnThatPage: ['prefixItems', 'oneOf', 'default'],
  provenance: 'DOC' as Provenance,
} as const;
